import { ButtonProps, Combobox } from "@altimateai/lego";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FilterType } from "../tags/types";
import { createFilter, getSavedFilters, updateFilter } from "@/helpers/apis";
import { useCallback, useEffect } from "react";
import { setSelectedColumns } from "./columnPickerSlice";
import { useColumnPickerContext } from "./types";
import { toast } from "react-toastify";
import { useAppState } from "@/modules/app/useAppContext";
import { formatUserName } from "@/helpers/utils";

const LocalStorageKey = "columnPicker_";
/**
 * This component is responsible for rendering the column picker dropdown
 * and handling the state of the selected columns on page load depending on the component from which it is rendered (using `applicableFor` prop).
 * Also it saves the disabled columns as a preset filter in the backend.
 */
const ColumnPicker = ({ buttonProps }: { buttonProps?: ButtonProps }) => {
  const {
    dispatch,
    state: { headers, applicableFor, columns },
  } = useColumnPickerContext();
  const { loggedinUser } = useAppState();

  const updateSelectedColumns = useCallback(
    (disabledColumns: string[]) => {
      dispatch(
        setSelectedColumns(
          new Set(headers.filter(h => !disabledColumns.includes(h.value)).map(h => h.value))
        )
      );
    },
    [dispatch, headers]
  );

  useEffect(() => {
    if (!applicableFor) return;
    // Load the data from localstorage until we get from the backend
    const data = localStorage.getItem(`${LocalStorageKey}${applicableFor}`) || "";
    updateSelectedColumns(data.split(","));
  }, [applicableFor, updateSelectedColumns]);

  // Unset the selected columns on page change
  useEffect(() => {
    return () => {
      dispatch(setSelectedColumns(new Set()));
    };
  }, [dispatch]);

  const {
    data,
    isPending: isLoading,
    refetch,
  } = useQuery({
    queryKey: [`COLUMN_PICKER_${applicableFor}`],
    queryFn: () =>
      getSavedFilters({
        applicableFor: applicableFor!,
        type: FilterType.COLUMN_PICKER,
      }),
    enabled: !!applicableFor,
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (data) {
      const disabledColumns = data?.presets[0]?.data.disabledColumns as string[] | undefined;
      if (disabledColumns) {
        updateSelectedColumns(disabledColumns);
        localStorage.setItem(`${LocalStorageKey}${applicableFor}`, disabledColumns.join(","));
      }
    }
  }, [data, applicableFor, updateSelectedColumns]);

  const { mutate } = useMutation({
    mutationFn: (enabledColumns: string[]) => {
      const disabledColumns = headers
        .filter(h => !enabledColumns.includes(h.value))
        .map(h => h.value);
      if (!data?.presets.length) {
        const userName = formatUserName(loggedinUser) || loggedinUser?.id || "unknown";
        return createFilter({
          applicable_for: applicableFor!,
          type: FilterType.COLUMN_PICKER,
          data: { disabledColumns },
          name: `${applicableFor} columns ${userName}`,
        });
      }

      return updateFilter({
        ...data.presets[0],
        data: { disabledColumns },
      });
    },
    onMutate: (enabledColumns: string[]) => {
      dispatch(setSelectedColumns(new Set(enabledColumns)));
    },
    onSuccess: () => {
      refetch();
    },
    onError: err => {
      toast(`Error saving the columns: ${err}`, { type: "error" });
    },
  });

  if (isLoading) return null;

  return (
    <Combobox
      buttonProps={{
        variant: "secondary",
        "aria-label": "Columns",
        className: "!al-w-40",
        ...buttonProps,
      }}
      multiSelect
      showApplyButton
      showOnlyPlaceholder
      placeholder={"Columns"}
      value={[...columns]}
      options={[...headers]}
      onChange={v => mutate(v as string[])}
    />
  );
};

export default ColumnPicker;
